<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {AlignLeft, CalendarDays, Clock, MapPin, StickyNote, Tag, User as UserIcon, UserCog} from "@lucide/vue";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import {Button} from "@/core/components/ui/button";
import CatalogIcon from "@/core/components/icon/CatalogIcon.vue";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {formatAgendaTime, parseAgendaDate} from "@/agenda/lib/agendaTime";
import {DEFAULT_APPOINTMENT_COLOR} from "@/agenda/constants";
import {appointmentDescription, appointmentLabel} from "@/agenda/lib/appointmentLabel";
import {emptyPatientSmsChoice, isPatientSmsChoiceValid, PatientSmsChoice} from "@/agenda/lib/patientSms";
import AgendaPatientSmsOption from "@/agenda/components/AgendaPatientSmsOption.vue";

const props = defineProps<{
    appointment: AgendaAppointment | null;
    loading?: boolean;
}>();

const emits = defineEmits<{
    (e: 'edit'): void;
    (e: 'reschedule'): void;
    (e: 'cancel-appointment', sms: PatientSmsChoice): void;
    (e: 'open-patient', customerId: number, beneficiaryId?: number): void;
}>();

const open = defineModel<boolean>('open', {required: true});

const cancelDialogOpen = ref(false);

const cancelSms = ref<PatientSmsChoice>(emptyPatientSmsChoice());

watch(cancelDialogOpen, (isOpen) => {
    if (isOpen) {
        cancelSms.value = emptyPatientSmsChoice();
    }
});

const color = computed(() => props.appointment?.appointment_type?.color ?? DEFAULT_APPOINTMENT_COLOR);

const label = computed(() => appointmentLabel(props.appointment));

const description = computed(() => appointmentDescription(props.appointment));

const dayLabel = computed(() => {
    return props.appointment
        ? parseAgendaDate(props.appointment.start_at).format('dddd D MMMM YYYY')
        : '';
});

const timeLabel = computed(() => {
    return props.appointment
        ? `${formatAgendaTime(props.appointment.start_at)} – ${formatAgendaTime(props.appointment.end_at)}`
        : '';
});

const patientLabel = computed(() => {
    if (!props.appointment) {
        return null;
    }

    const beneficiary = props.appointment.beneficiary?.name;
    const customer = props.appointment.customer?.name;

    return beneficiary ? `${beneficiary} (réservé par ${customer ?? 'le patient'})` : customer;
});

const operatorLabel = computed(() => {
    // The access point names every member, an account-less one included, and
    // is the operator's identity. The user object answers for the older
    // appointments.
    const accessPoint = props.appointment?.operator_access_point;

    if (accessPoint) {
        return accessPoint.name
            ?? (`${accessPoint.first_name ?? ''} ${accessPoint.last_name ?? ''}`.trim() || null);
    }

    const operator = props.appointment?.operator;

    if (!operator) {
        return null;
    }

    return operator.name ?? (`${operator.first_name ?? ''} ${operator.last_name ?? ''}`.trim() || null);
});

const statusBadge = computed(() => {
    switch (props.appointment?.status) {
        case 'pending':
            return {label: 'À confirmer', classes: 'bg-orange-light text-gray-dark'};
        case 'canceled':
            return {label: 'Annulé', classes: 'bg-red-100 text-red-600'};
        case 'refused':
            return {label: 'Refusé', classes: 'bg-red-100 text-red-600'};
        default:
            return {label: 'Confirmé', classes: 'bg-secondary-lightest text-secondary-darkest'};
    }
});

const isActive = computed(() => {
    return props.appointment?.status === 'pending' || props.appointment?.status === 'confirmed';
});
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent v-if="appointment" class="max-w-md">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <span class="size-3 shrink-0 rounded-full" :style="{backgroundColor: color}" />
          <CatalogIcon :name="appointment.appointment_type?.icon" class="size-5" />
          {{ label }}
          <span class="ml-auto rounded px-2 py-0.5 text-xs font-medium" :class="statusBadge.classes">
            {{ statusBadge.label }}
          </span>
        </DialogTitle>
        <DialogDescription v-if="appointment.origin === 'online'">
          Rendez-vous pris en ligne par le patient.
        </DialogDescription>
      </DialogHeader>
      <div class="flex flex-col gap-2.5 text-sm">
        <p class="flex items-center gap-2 capitalize">
          <CalendarDays class="size-4 shrink-0 text-gray-400" />
          {{ dayLabel }}
        </p>
        <p class="flex items-center gap-2 tabular-nums">
          <Clock class="size-4 shrink-0 text-gray-400" />
          {{ timeLabel }}
        </p>
        <p v-if="appointment.appointment_type" class="flex items-center gap-2">
          <Tag class="size-4 shrink-0 text-gray-400" />
          {{ appointment.appointment_type.name }}
        </p>
        <p v-if="patientLabel" class="flex items-center gap-2">
          <UserIcon class="size-4 shrink-0 text-gray-400" />
          <!-- Each name opens its own file; both routes hang off the booking
               customer, so without one the label stays plain text. -->
          <span v-if="appointment.customer">
            <template v-if="appointment.beneficiary?.name">
              <button
                type="button"
                class="font-medium !text-primary hover:underline"
                @click="emits('open-patient', appointment.customer.id, appointment.beneficiary.id)"
              >
                {{ appointment.beneficiary.name }}
              </button>
              (réservé par
              <button
                type="button"
                class="font-medium !text-primary hover:underline"
                @click="emits('open-patient', appointment.customer.id)"
              >
                {{ appointment.customer.name ?? 'le patient' }}</button>)
            </template>
            <button
              v-else
              type="button"
              class="font-medium !text-primary hover:underline"
              @click="emits('open-patient', appointment.customer.id)"
            >
              {{ appointment.customer.name }}
            </button>
          </span>
          <template v-else>
            {{ patientLabel }}
          </template>
        </p>
        <p v-if="operatorLabel" class="flex items-center gap-2">
          <UserCog class="size-4 shrink-0 text-gray-400" />
          {{ operatorLabel }}
        </p>
        <p v-if="description" class="flex items-start gap-2 whitespace-pre-line">
          <AlignLeft class="mt-0.5 size-4 shrink-0 text-gray-400" />
          {{ description }}
        </p>
        <p v-if="appointment.location" class="flex items-center gap-2">
          <MapPin class="size-4 shrink-0 text-gray-400" />
          {{ appointment.location }}
        </p>
        <p v-if="appointment.pharmacist_memo" class="flex items-start gap-2 text-gray-600">
          <StickyNote class="mt-0.5 size-4 shrink-0 text-gray-400" />
          {{ appointment.pharmacist_memo }}
        </p>
      </div>
      <DialogFooter v-if="isActive">
        <Button
          variant="ghost"
          class="text-red-500 hover:text-red-600"
          :disabled="loading"
          @click="cancelDialogOpen = true"
        >
          Annuler le RDV
        </Button>
        <Button variant="outline" :disabled="loading" @click="emits('reschedule')">
          Déplacer
        </Button>
        <Button variant="outline" :disabled="loading" @click="emits('edit')">
          Modifier
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  <AlertDialog v-model:open="cancelDialogOpen">
    <!-- z-[60]: stacked above the detail Dialog (z-50), whose portal can
         land after this one in the DOM. -->
    <AlertDialogContent class="z-[60]">
      <AlertDialogHeader>
        <AlertDialogTitle>Annuler ce rendez-vous ?</AlertDialogTitle>
        <AlertDialogDescription>
          Le rendez-vous « {{ label }} » {{ patientLabel ? `de ${patientLabel}` : '' }} sera annulé.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AgendaPatientSmsOption
        v-if="appointment?.customer"
        v-model="cancelSms"
        :appointment-id="appointment.id"
        event="canceled"
      />
      <AlertDialogFooter>
        <AlertDialogCancel>Retour</AlertDialogCancel>
        <AlertDialogAction
          class="bg-red hover:bg-red/90"
          :disabled="loading || !isPatientSmsChoiceValid(cancelSms)"
          @click="emits('cancel-appointment', cancelSms)"
        >
          Annuler le rendez-vous
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
